import store from '../data/store';
import { doctorBlack, doctorSecondary, nurseBlack, nurseSecondary } from '@/assets';
import { formatterFilter } from '@/utils/luxon-formater';

export function showSuccessAlert (message) {
	store.commit('alerts/add', { type: 'success', message });
	store.commit('alerts/clear', { type: 'success', message });
}

export function showErrorAlert (message, durationSeconds = 2) {
	store.commit('alerts/add', { type: 'error', message });
	setTimeout(() => {
	store.commit('alerts/clear', { type: 'success', message });
	}, durationSeconds * 1000);
}

/**
 * Returns an JSON object for defining props property in datatable
 *
 * @param  dataTableComponentName  dataTable name from the component what we want to handel the props name
 * @return props object
 * @see         props
 */
export function getDataTableFooterProps (dataTableComponentName) {
	// If you want to handle specific data table add the parameters and name dataTableComponentName  to the switch case
	var tablePropsDefault = {
		'items-per-page-options': [5, 10, 25, 50, -1],
		'items-per-page-text': this.$t('rows-per-page'),
	};
	switch (dataTableComponentName) {
		case 'systemLogsTable': {
			var tableProps = {
				'items-per-page-options': [-1, 100, 500, 1000, 5000],
				'items-per-page-text': this.$t('rows-per-page'),
			};
			return tableProps;
		}
		default: {
			return tablePropsDefault;
		}
	}
}
/**
 * Returns a class for defining odd or even rows in tr in v-data-table
 *
 * @param  dataTableIndex  index from the data table to determine if the rows are even or odd , so we apply a style.
 * @see    class
 */
export function getRowsForDataTable (dataTableIndex, selectedRow = false) {
	if (!selectedRow) {
		if (dataTableIndex % 2 === 1) {
			return 'odd-row';
		}
		if (dataTableIndex % 2 === 0) {
			return 'even-row';
		}
	} else {
		return 'selected-row';
	}
}

export function downloadCSVWithKeys (objArray, filename, { timeSpent = 0, ids = [], data = [], timestamps = [] }) {
	if (!Array.isArray(objArray) || objArray.length === 0 || !Array.isArray(data) || data.length === 0) {
		// console.error('Invalid input. Please provide a non-empty array of objects and an array of keys.');
		return;
	}

	const allHeaders = [...ids, ...data, timeSpent, ...timestamps];

	const headerRow = allHeaders
		.map((header) => {
			const capitalizedHeader = String(header).charAt(0).toUpperCase() + String(header).slice(1);
			// Insert a space before every capital letter after the first one
			const formattedHeader = capitalizedHeader.replace(/(?<![A-Z])([A-Z])/g, ' $1').trim();
			return `"${formattedHeader.replace(/"/g, '""')}"`;
		})
		.join(',');

	let csvString = headerRow + '\r\n';

	objArray.forEach((item) => {
		const dataRow = allHeaders
			.map((header) => {
				let value = item[header] !== undefined && item[header] !== null ? String(item[header]).trim() : '';

				// Apply special formatting based on the header's category
				if (ids.includes(header)) {
					// Prepend '#' for ID values
					value = `#${value}`;
				} else if (timestamps.includes(header)) {
					// Format timestamp values using the provided function
					const dateObject = new Date(item[header]);
					const options = {
						month: 'short',
						day: '2-digit',
						year: 'numeric',
						hour: '2-digit',
						minute: '2-digit',
						hour12: true,
					};
					value = dateObject.toLocaleString('en-US', options);
				} else if (header === timeSpent) {
					value = formatDuration(item[header]);
				}

				// Escape double quotes and enclose the value in quotes
				return `"${value.replace(/"/g, '""')}"`;
			})
			.join(',');

		csvString += dataRow + '\r\n';
	});

	const now = new Date();
	const formattedDate =
		now.getFullYear() +
		'-' +
		(now.getMonth() + 1).toString().padStart(2, '0') +
		'-' +
		now.getDate().toString().padStart(2, '0');

	const datedFilename = `${filename.replace('.csv', '')}_${formattedDate}.csv`;

	const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
	const link = document.createElement('a');

	if (link.download !== undefined) {
		const url = URL.createObjectURL(blob);
		link.setAttribute('href', url);
		link.setAttribute('download', datedFilename);
		link.style.visibility = 'hidden';
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	} else {
		// console.error('The \'download\' attribute is not supported by this browser.');
	}
}

export function shortenText (txt, maxLength = 55, maxWordLength = 15, endString = '...') {
	if (txt.length > maxLength) {
		const wordArray = txt.split(' ');
		var shortText = '';
		for (const word of wordArray) {
			const remainingLength = maxLength - shortText.length;
			if (word.length > remainingLength && word.length > maxWordLength) {
				shortText += word.substring(0, remainingLength);
			} else if ((shortText + word).length <= maxLength) {
				shortText += word + ' ';
			} else {
				break;
			}
		}
		return shortText.trim() + endString;
	}
	return txt;
}

export function formatTimestamp (val, res = 'date_med') {
	const dateObject = new Date(val);
	return formatterFilter(dateObject, res, this.$i18n.locale, null);
}

export function getDoctorIcon (status) {
	switch (status) {
		case 1:
			return doctorBlack;
		default:
			return doctorSecondary;
	}
}
export function getNurseIcon (status) {
	switch (status) {
		case 1:
			return nurseBlack;
		default:
			return nurseSecondary;
	}
}
export function toggleHeaders () {
	const table = this.$refs.table;
	if (table) {
		const openCache = table?.$vnode.componentInstance.openCache;
		if (openCache) {
			Object.keys(openCache).forEach((group) => {
				this.$set(openCache, group, false);
			});
		}
	}
}
export function calculateAge (dateString) {
	var today = new Date();
	var birthDate = new Date(dateString?.split('T')[0]);
	var age = today.getFullYear() - birthDate.getFullYear();
	var m = today.getMonth() - birthDate.getMonth();
	if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
		age--;
	}
	return age;
}

/**
 * Converts a duration in seconds into a MM:SS formatted string.
 *
 * @param {number} input - Total time in seconds.
 * @returns {string} Time string formatted as "mm:ss".
 *
 * @example
 * formatSecondsToTime(90) // "01:30"
 * formatSecondsToTime(8)  // "00:08"
 */
export function formatSecondsToTime (input) {
	const mins = Math.floor(input / 60);
	const secs = input % 60;
	return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Converts a "MM:SS" time string into total seconds.
 *
 * @param {string} timeString - A string in "mm:ss" format.
 * @returns {number} Total seconds as a number. Returns 0 if input is invalid.
 *
 * @example
 * formatTimeToSeconds("02:15") // 135
 * formatTimeToSeconds("bad")   // 0
 */
export function formatTimeToSeconds (timeString) {
	const [mins, secs] = timeString.split(':').map(Number);
	if (isNaN(mins) || isNaN(secs)) return 0;
	return mins * 60 + secs;
}

/**
 * Converts seconds into a human-readable duration format like "1h 15min 16sec".
 *
 * @param {number} seconds - Duration in seconds.
 * @returns {string} Formatted duration string.
 *
 * @example
 * formatDuration(4516) // "1h 15min 16sec"
 * formatDuration(200)  // "3min 20sec"
 * formatDuration(45)   // "45sec"
 */
export function formatDuration (seconds) {
	const h = Math.floor(seconds / 3600);
	const m = Math.floor((seconds % 3600) / 60);
	const s = seconds % 60;

	const parts = [];
	if (h > 0) parts.push(`${h}h`);
	if (m > 0) parts.push(`${m}min`);
	if (s > 0 || parts.length === 0) parts.push(`${s}sec`);

	return parts.join(' ');
}

/**
 * Extracts a single value/unit pair from a string like "112/49 mmHg / 59 bpm"
 * according to the desired unit system ('metric' or 'imperial').
 * Returns only one { value, unit } object that matches the system.
 *
 * @param {string} valueStr - The raw string containing values
 * @param {'metric'|'imperial'} unitSystem - Desired unit system
 * @returns {{ value: string, unit: string } | null}
 */
export function getValueByUnit (valueStr, unitSystem, concentrationSystem) {
	const parts = valueStr.split(' / ');
	if (parts.length === 1) {
		// is an universal value
		const firstPart = parts[0].split(' ');
		return { value: firstPart[0], unit: firstPart[1] };
	} else {
		const metricKeywords = ['kg', '°C', 'scale', 'mmHg'];
		const imperialKeywords = ['lbs', '°F', 'mmHg'];

		const [valueFirst, unitFirst] = parts[0].split(' ');
		const [valueSecond, unitSecond] = parts[1].split(' ');

		if (unitSystem === 'METRIC') {
			return metricKeywords.includes(unitFirst)
				? { value: valueFirst, unit: unitFirst }
				: { value: valueSecond, unit: unitSecond };
		} else if (unitSystem === 'IMPERIAL') {
			return imperialKeywords.includes(unitFirst)
				? { value: valueFirst, unit: unitFirst }
				: { value: valueSecond, unit: unitSecond };
		}

		var firstUnit = valueStr.split(' / ')[0].split(' ').slice(1).join('');
		var secondUnit = valueStr.split(' / ')[1].split(' ').slice(1).join('');

		var firstValue = valueStr.split(' / ')[0].split(' ')[0];
		var secondValue = valueStr.split(' / ')[1].split(' ')[0];

		return concentrationSystem.toLowerCase() === firstUnit.toLowerCase()
			? { value: firstValue, unit: firstUnit }
			: { value: secondValue, unit: secondUnit };
	}
}

export function getFullName (user) {
	return user.firstName + ' ' + user.lastName;
}

export function months () {
	const monthKeys = [
		'january',
		'february',
		'march',
		'april',
		'may',
		'june',
		'july',
		'august',
		'september',
		'october',
		'november',
		'december',
	];

	const monthsMapped = monthKeys.map((key, index) => ({
		text: `${index + 1} - ${this.$t(key)}`,
		value: index + 1,
	}));

	return monthsMapped;
}

export async function getHospitals () {
	this.loadingHospitals = true;
	await this.$store.dispatch('hospitals/getHospitals').then(() => {
		this.loadingHospitals = false;
	});
}

export function getBrowserTimeZone () {
	return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

export function monthName (date) {
	return new Date(date).toLocaleString('default', { month: 'short' });
}

export function getMonthName (monthNumber) {
	const months = [
		'January',
		'February',
		'March',
		'April',
		'May',
		'June',
		'July',
		'August',
		'September',
		'October',
		'November',
		'December',
	];

	// Convert to zero-based index
	return months[monthNumber - 1] || null;
}

export function formatTime (dateString) {
	const date = new Date(dateString); // Convert the string to a Date object
	return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
}
